import { Controller } from "@hotwired/stimulus";
import { CONNECTIVITY_CHANGE, hasInternetAccess, seemsOffline, visit } from "../utils/connectivity";
import offlineProgressSync from "../src/react/helpers/OfflineProgressSync";

const CASE_HREF_PATTERN = /\/document_set_document_relations\/[^/?#]+/;
const CALIBRATE_START_HREF = /\/assessment\/rosterings\/\d+\/(?:instructions|honor_code)(?:[/?#]|$)/;
const DEMO_ROSTERING_HREF = /\/assessment\/demo_rosterings\/\d+(?:[/?#]|$)/;
const QUIZ_GENERATOR_OFFLINE_ACTION =
  /quiz-generator-form#(?:openNameModal|goToReview)/;
const QUIZ_GENERATOR_HREF = /\/quizzes\/new(?:[/?#]|$)/;

export function practiceSmartFormNeedsNetwork(action) {
  if (!action) return false;
  if (/reset_open_question/.test(action)) return true;
  if (/\/quiz_item_responses\/\d+\/reset/.test(action)) return true;
  if (/\/quizzes\/(?:start_essential|retake_essential|schedule_essential)/.test(action)) return true;
  if (/\/quizzes\/(?:add_to_todo_list|number_of_items|validate_name)/.test(action)) return false;
  return /\/quizzes\/?(?:\?|#|$)/.test(action);
}

let activeOfflineNavController = null;

export function quizGeneratorNavigationBlocked() {
  return seemsOffline() || !navigator.onLine;
}

function showQuizGeneratorUnavailable(trigger) {
  const controller = activeOfflineNavController;
  if (controller?.hasFeatureUnavailableModalTarget) controller.showFeatureUnavailable(trigger);
}

if (typeof document !== "undefined") {
  document.addEventListener(
    "click",
    (event) => {
      if (!quizGeneratorNavigationBlocked()) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button) return;
      const anchor = event.target.closest?.("a[href]");
      if (!anchor || anchor.target === "_blank" || !QUIZ_GENERATOR_HREF.test(anchor.href)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      showQuizGeneratorUnavailable(anchor);
    },
    true
  );
  document.addEventListener("turbo:before-visit", (event) => {
    if (!QUIZ_GENERATOR_HREF.test(event.detail?.url || "") || !quizGeneratorNavigationBlocked()) return;
    event.preventDefault();
    showQuizGeneratorUnavailable(document.activeElement);
  });
}

export default class extends Controller {
  static targets = ["offlineModal", "featureUnavailableModal", "featureUnavailableOkButton"];

  connect() {
    activeOfflineNavController = this;
    this.boundDismissFeatureUnavailable = () => this.dismissFeatureUnavailable();
    this.boundBlockOfflineSubmit = (event) => this.blockOfflineSubmit(event);
    this.boundResumePendingVisit = (event) => this.resumePendingVisit(event);
    window.addEventListener("online", this.boundDismissFeatureUnavailable);
    document.addEventListener(CONNECTIVITY_CHANGE, this.boundResumePendingVisit);
    this.element.addEventListener("submit", this.boundBlockOfflineSubmit, true);
  }

  disconnect() {
    if (activeOfflineNavController === this) activeOfflineNavController = null;
    window.removeEventListener("online", this.boundDismissFeatureUnavailable);
    document.removeEventListener(CONNECTIVITY_CHANGE, this.boundResumePendingVisit);
    this.element.removeEventListener("submit", this.boundBlockOfflineSubmit, true);
  }

  openSupport(event) {
    event.preventDefault();
    event.stopPropagation();
    if (seemsOffline()) {
      if (this.hasFeatureUnavailableModalTarget) this.showFeatureUnavailable(event.currentTarget);
      return;
    }

    const url = event.params.supportUrl;
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  interceptOfflineNavigation(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (event.button) return;
    if (!seemsOffline()) return;

    const support = event.target.closest?.(".support_btn");
    if (support) {
      this.blockOfflineAction(event, event.target.closest?.("button, a") || support);
      return;
    }

    if (event.defaultPrevented) return;

    const honorContinue = event.target.closest?.(".honor-continue-btn");
    if (honorContinue) {
      this.blockOfflineAction(event, honorContinue);
      return;
    }

    const quizGeneratorControl = event.target.closest?.("button, a, input[type='submit']");
    const quizGeneratorAction = quizGeneratorControl?.getAttribute("data-action") || "";
    if (quizGeneratorControl && QUIZ_GENERATOR_OFFLINE_ACTION.test(quizGeneratorAction)) {
      this.blockOfflineAction(event, quizGeneratorControl);
      return;
    }

    const anchor = event.target.closest?.("a[href]");
    const href = anchor?.getAttribute("href");
    if (anchor && anchor.target !== "_blank" && href && CALIBRATE_START_HREF.test(href)) {
      this.blockOfflineAction(event, anchor);
      return;
    }

    const form = event.target.closest?.("form");
    if (form && this.offlineBlockedForm(form)) {
      this.blockOfflineAction(event, event.target.closest?.("button, input") || form);
      return;
    }

    if (anchor?.target === "_blank") return;
    if (!href || !CASE_HREF_PATTERN.test(href)) return;

    event.preventDefault();
    event.stopPropagation();
    this.routeOffline(anchor.href);
  }

  blockOfflineSubmit(event) {
    if (!seemsOffline()) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !this.offlineBlockedForm(form)) return;

    this.blockOfflineAction(event, event.submitter || form);
  }

  offlineBlockedForm(form) {
    if (form.closest(".support_btn")) return true;
    const action = form.getAttribute("action") || "";
    if (CALIBRATE_START_HREF.test(action) || DEMO_ROSTERING_HREF.test(action)) return true;

    const method = (form.getAttribute("method") || form.method || "get").toLowerCase();
    return method === "post" && practiceSmartFormNeedsNetwork(action);
  }

  blockOfflineAction(event, trigger) {
    event.preventDefault();
    event.stopPropagation();
    if (this.hasFeatureUnavailableModalTarget) this.showFeatureUnavailable(trigger);
  }

  resumePendingVisit(event) {
    if (event.detail.online && offlineProgressSync.pendingVisit) this.proceedPending();
  }

  dismissFeatureUnavailable() {
    if (!this.hasFeatureUnavailableModalTarget) return;
    if (this.featureUnavailableModalTarget.classList.contains("hidden")) return;
    this.closeFeatureUnavailable();
  }

  async routeOffline(href, { cached, resumeAfterClose = false } = {}) {
    if (cached ?? (await this.isCached(href))) {
      this.visitOffline(href);
      return true;
    }
    offlineProgressSync.pendingVisit = href;
    this.resumeAfterClose = resumeAfterClose;
    this.offlineModalTarget.classList.remove("hidden");
    return false;
  }

  proceedPending() {
    const href = offlineProgressSync.pendingVisit;
    offlineProgressSync.pendingVisit = null;
    this.offlineModalTarget.classList.add("hidden");
    if (href) this.visitOffline(href);
  }

  visitOffline(href) {
    offlineProgressSync.allowNextUnload();
    visit(href);
  }

  async isCached(href) {
    try {
      return !!(await caches.match(href, { ignoreVary: true }));
    } catch {
      return false;
    }
  }

  closeOffline() {
    this.offlineModalTarget.classList.add("hidden");
    if (!this.resumeAfterClose) offlineProgressSync.pendingVisit = null;
  }

  showFeatureUnavailable(trigger) {
    this.lastFocusedControl = trigger;
    this.featureUnavailableModalTarget.classList.remove("hidden");
    this.featureUnavailableModalTarget.classList.add("gen-modal--front");
    this.featureUnavailableOkButtonTarget.focus();
  }

  closeFeatureUnavailable() {
    this.featureUnavailableModalTarget.classList.add("hidden");
    this.featureUnavailableModalTarget.classList.remove("gen-modal--front");
    this.lastFocusedControl?.focus();
  }

  async showStudyModeNotice(href, trigger) {
    const result = await window.Swal?.fire({
      title: "You're offline",
      html: "<p>Quiz mode needs an internet connection. Study mode will be launched instead.</p>",
      showCancelButton: true,
      confirmButtonText: "Continue",
      cancelButtonText: "Cancel",
      customClass: {
        confirmButton: "aq-button-2 medium with-margin",
        cancelButton: "aq-button-2 medium with-margin outline black",
      },
      buttonsStyling: false,
    });

    if (!result?.isConfirmed) return trigger?.focus();

    offlineProgressSync.allowNextUnload();
    visit(href);
  }

  async retry() {
    if (await hasInternetAccess(1500)) return this.proceedPending();
    window.Swal?.fire({
      title: "Still Offline",
      html: "<p>Please check your internet connection and try again.</p>",
      confirmButtonText: "OK",
      customClass: { confirmButton: "aq-button-2 medium with-margin" },
      buttonsStyling: false,
    });
  }
}
